import React, { useMemo } from 'react';
import Iconify from '@emfood/shared/src/components/iconify';
import { useAuthContext, useHasBuyerOrg, useHasDistributorSite } from '@emfood/shared';

import { Chip } from '@mui/material';

import { paths } from 'src/routes/paths';
import { ValidRoutePath } from 'src/routes/sections';

import { ROLES, PERMISSIONS } from 'src/api/roles-config';

import SvgColor from 'src/components/svg-color';

const iconifyIcon = (name: string) => <Iconify icon={name} />;

const icon = (name: string) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
);

const ICONS = {
  seasonalCalendar: icon('seasonal_calendar_icon'),
  managment: iconifyIcon('ph:arrows-left-right-fill'),
  products: iconifyIcon('dashicons:products'),
  producers: iconifyIcon('fa-solid:apple-alt'),
  sources: iconifyIcon('basil:location-solid'),
  distributors: iconifyIcon('carbon:sysplex-distributor'),
  buyers: iconifyIcon('teenyicons:basket-solid'),
  buyersOrders: iconifyIcon('mingcute:clipboard-fill'),
  regions: iconifyIcon('material-symbols:source-environment-rounded'),
  organiztaion: iconifyIcon('grommet-icons:organization'),
  news: iconifyIcon('bxs:news'),
  surplus: iconifyIcon('mdi:truck'),
  markers: iconifyIcon('mingcute:safe-shield-2-fill'),
  issueLog: iconifyIcon('solar:flag-2-bold'),
  invoices: iconifyIcon('mdi:invoice-text'),
  reporting: iconifyIcon('material-symbols:bar-chart-4-bars'),
  stories: iconifyIcon('material-symbols:book-ribbon'),
};

// ----------------------------------------------------------------------

interface NavItem {
  title: string;
  path: ValidRoutePath;
  icon: React.ReactNode;
  children?: NavItem[];
  info?: React.ReactNode;
}

interface NavSection {
  subheader: string;
  roles?: string[];
  permissions?: string[];
  distributorSiteRequired?: boolean;
  items: NavItem[];
}

export function useNavData() {
  const { permissions, roles } = useAuthContext();
  const { buyerOrg } = useHasBuyerOrg();
  const { hasDistributorSite, distributorOrg } = useHasDistributorSite();

  const data = useMemo(() => {
    const navData: NavSection[] = [
      // OVERVIEW
      // ----------------------------------------------------------------------
      // {
      //   subheader: '',
      //   items: [{ title: 'Dashboard', path: paths.dashboard.root, icon: ICONS.dashboard }],
      // },
      {
        subheader: 'Organisation',
        roles: [ROLES.DISTRIBUTOR_ADMIN, ROLES.PRODUCER_ADMIN, ROLES.DATA_MANAGER],
        items: [
          {
            title: 'My Organisation',
            path: paths.dashboard.suppliers.supplierDetails(
              distributorOrg?.id || ''
            ) as ValidRoutePath,
            // TODO: Find a way to avoid casting to string and ensure dynamic route type safety
            icon: ICONS.organiztaion,
          },
        ],
      },
      {
        subheader: 'Organisation',
        roles: [ROLES.BUYER_ADMIN],
        items: [
          {
            title: 'My Organisation',
            path: paths.dashboard.buyers.buyerDetails(buyerOrg?.id || '') as ValidRoutePath,
            icon: ICONS.organiztaion,
          },
        ],
      },
      // {
      //   subheader: 'Organisation',
      //   roles: [ROLES.PRODUCER_ADMIN],
      //   items: [
      //     {
      //       title: 'My Organisation',
      //       path: paths.dashboard.producer.producerDetails(
      //         sourceSite?.producerId || ''
      //       ) as ValidRoutePath,
      //       icon: ICONS.organiztaion,
      //     },
      //   ],
      // },

      // USER MANAGEMENT
      // ----------------------------------------------------------------------
      {
        subheader: 'Users',
        // roles: [ROLES.ADMIN, ROLES.ENTEGRA_USER, ROLES.DISTRIBUTOR_ADMIN, ROLES.PRODUCER_ADMIN],
        permissions: [PERMISSIONS.USERS_READ],
        items: [
          {
            title: 'Users',
            path: '/user-managment',
            icon: ICONS.managment,
            // {
            //   title: 'Invite User',
            //   path: paths.dashboard.userManagment.inviteUser,
            //   // permissions: [PERMISSIONS.USER_CRUD],
            // },
          },
        ],
      },
      {
        subheader: 'Data Mapper',
        roles: [ROLES.ADMIN, ROLES.DISTRIBUTOR_ADMIN, ROLES.DATA_MANAGER],
        items: [
          {
            title: 'Data Mapper',
            path: '/data-mapper' as ValidRoutePath,
            icon: iconifyIcon('mdi:table-arrow-right'),
          },
        ],
      },
      {
        subheader: 'Products',
        roles: [ROLES.ADMIN, ROLES.DISTRIBUTOR_ADMIN, ROLES.DATA_MANAGER, ROLES.ENTEGRA_USER],
        items: [
          {
            title: 'Products',
            path: '/products',
            icon: ICONS.products,
            // children: [
            //   {
            //     title: 'Products',
            //     path: paths.dashboard.products.root,
            //     // permissions: [PERMISSIONS.INVOICE_READ],
            //   },
            //   // {
            //   //   title: 'Add Product',
            //   //   path: paths.dashboard.products.addProduct,
            //   // },
            // ],
          },
        ],
      },
      {
        subheader: 'Suppliers',
        roles: [ROLES.ADMIN, /* ROLES.ENTEGRA_USER, */ ROLES.ENTEGRA_PRICE_ADMIN],
        items: [
          {
            title: 'Suppliers',
            path: '/suppliers',
            icon: ICONS.distributors,
          },
        ],
      },
      {
        subheader: 'Buyers',
        roles: [
          ROLES.ADMIN,
          ROLES.ENTEGRA_USER,
          ROLES.ENTEGRA_PRICE_ADMIN,
          ROLES.DISTRIBUTOR_ADMIN,
          ROLES.DATA_MANAGER,
        ],
        items: [
          {
            title: 'Buyers',
            path: '/buyers',
            icon: ICONS.buyers,
          },
        ],
      },
      {
        subheader: 'Buyer Orders',
        roles: [
          ROLES.ADMIN,
          ROLES.DISTRIBUTOR_ADMIN,
          ROLES.BUYER_ADMIN,
          ROLES.DATA_MANAGER,
          ROLES.ENTEGRA_USER,
        ],
        distributorSiteRequired: true,
        items: [
          {
            title: 'Buyer Orders',
            path: '/buyer-orders',
            icon: ICONS.buyersOrders,
          },
        ],
      },
      {
        subheader: 'Issue Log',
        roles: [ROLES.DISTRIBUTOR_ADMIN, ROLES.DATA_MANAGER, ROLES.BUYER_ADMIN],
        items: [
          {
            title: 'Issue Log',
            path: '/order-issues',
            icon: ICONS.issueLog,
          },
        ],
      },
      {
        subheader: 'News',
        permissions: [PERMISSIONS.NEWS_READ],
        items: [
          {
            title: 'News & Articles',
            path: '/news',
            icon: ICONS.news,
          },
        ],
      },
      {
        subheader: 'Surplus',
        roles: [ROLES.ADMIN, ROLES.DISTRIBUTOR_ADMIN, ROLES.DATA_MANAGER],
        items: [
          {
            title: 'Surplus',
            path: '/surplus',
            icon: ICONS.surplus,
          },
        ],
      },
      {
        subheader: 'Seasons',
        roles: [ROLES.ADMIN, ROLES.DISTRIBUTOR_ADMIN, ROLES.PRODUCER_ADMIN, ROLES.DATA_MANAGER],
        items: [
          {
            title: 'Seasons',
            path: '/seasonal-calendar',
            icon: ICONS.seasonalCalendar,
          },
        ],
      },
      {
        subheader: 'Markers',
        roles: [ROLES.ADMIN, ROLES.PRODUCER_ADMIN],
        items: [
          {
            title: 'Markers',
            path: '/markers',
            icon: ICONS.markers,
          },
        ],
      },
      {
        subheader: 'Reporting',
        roles: [
          ROLES.ADMIN,
          ROLES.DISTRIBUTOR_ADMIN,
          ROLES.PRODUCER_ADMIN,
          ROLES.BUYER_ADMIN,
          ROLES.ENTEGRA_USER,
        ],
        items: [
          {
            title: 'Reporting',
            path: '/reporting',
            icon: ICONS.reporting,
          },
        ],
      },
      {
        subheader: 'Analytics',
        permissions: [PERMISSIONS.INVOICES_READ],
        items: [
          {
            title: 'Analytics',
            path: '/invoices',
            icon: ICONS.invoices,
          },
        ],
      },
      {
        subheader: 'Stories',
        permissions: [PERMISSIONS.STORIES_READ],
        items: [
          {
            title: 'Stories',
            path: '/stories',
            icon: ICONS.stories,
            info: (
              <Chip
                label="Beta"
                size="small"
                variant="outlined"
                color="warning"
                sx={{ fontSize: '0.6rem', height: '16px' }}
              />
            ),
          },
        ],
      },
    ];

    const filterNavData = (config: any[]) =>
      config.filter((item) => {
        // Check permissions
        if (
          item.permissions &&
          !item.permissions.some((permission: string) => permissions?.includes(permission))
        ) {
          return false;
        }

        // Check roles
        if (item.roles && !item.roles.some((role: string) => roles?.includes(role))) {
          return false;
        }

        // Check distributorSiteRequired
        if (
          item.distributorSiteRequired &&
          !hasDistributorSite &&
          !item.roles.includes(ROLES.ADMIN)
        ) {
          return false;
        }

        if (item.children) {
          item.children = filterNavData(item.children);
        } else if (item.items) {
          item.items = filterNavData(item.items);
        }

        return true;
      });
    const result = filterNavData(navData);

    return result;
  }, [buyerOrg?.id, distributorOrg?.id, hasDistributorSite, permissions, roles]);

  return data;
}
